import posthog from "posthog-js";

export const FLAG_NAMES = [
  "experimentCommandK",
  "commandKToast",
  "checkboxes",
  "debugTools",
  "aiCorner",
  "ydocFromFirestore",
  "structuredOutput",
  "blogDemo",
  "slas2025",
  "compareVersion",
  "aiWithValidation",
  "aiModelSelection",
  "aiModelSelectionUI",
  "runner-control-panel-improvements",
  "csvUploads",
  "firestoreSync",
  "high-resolution-labware",
  "sandboxChat",
  "hidePortfolio",
  "workspaceConfig",
  "hideLegacyConfigs",
] as const;

export type FlagName = (typeof FLAG_NAMES)[number];

/**
 * Flags that are auto-enabled for members of an "automation team"
 * (team.isAutomationTeam === true). Resolved dynamically — adding a
 * new flag here applies to all existing automation teams on next load.
 */
export const AUTOMATION_FLAGS: readonly FlagName[] = [
  "aiWithValidation",
  "runner-control-panel-improvements",
  "csvUploads",
  "high-resolution-labware",
  "firestoreSync",
  "hidePortfolio",
] as const;

export function automationFlagDefaults(): Record<string, boolean> {
  return Object.fromEntries(AUTOMATION_FLAGS.map((f) => [f, true]));
}

export const experimentCommandKEnabled = () =>
  posthog.isFeatureEnabled("experimentCommandK");

export const commandKToastEnabled = () =>
  posthog.isFeatureEnabled("commandKToast");

export const checkboxesEnabled = () => posthog.isFeatureEnabled("checkboxes");

export const debugToolsEnabled = () => posthog.isFeatureEnabled("debugTools");

export const aiCornerEnabled = () => posthog.isFeatureEnabled("aiCorner");

export const ydocFromFirestore = () =>
  posthog.isFeatureEnabled("ydocFromFirestore");

export const structuredOutputEnabled = () =>
  posthog.isFeatureEnabled("structuredOutput");

export const blogDemoEnabled = () => posthog.isFeatureEnabled("blogDemo");

export const slas2025Enabled = () => posthog.isFeatureEnabled("slas2025");

export const compareVersionEnabled = () =>
  posthog.isFeatureEnabled("compareVersion");

export const aiWithValidationEnabled = () =>
  posthog.isFeatureEnabled("aiWithValidation");

export const aiModelSelectionEnabled = () =>
  posthog.isFeatureEnabled("aiModelSelection");

export const aiModelSelectionUIEnabled = () =>
  posthog.isFeatureEnabled("aiModelSelectionUI");

export const runnerControlPanelImprovementsEnabled = () =>
  posthog.isFeatureEnabled("runner-control-panel-improvements");

export const csvUploadsEnabled = () => posthog.isFeatureEnabled("csvUploads");

export const firestoreSyncEnabled = () =>
  posthog.isFeatureEnabled("firestoreSync");

export const highResolutionLabwareEnabled = () =>
  posthog.isFeatureEnabled("high-resolution-labware");

export const sandboxChatEnabled = () =>
  posthog.isFeatureEnabled("sandboxChat");

export const hidePortfolioEnabled = () =>
  posthog.isFeatureEnabled("hidePortfolio");

/** @deprecated No longer feature-gated — workspace config is always available */
export const workspaceConfigEnabled = () => true;

/**
 * Transition flag for deprecating legacy (file-uploaded) configs. When on, the
 * old configs are hidden from every config picker and the legacy upload entry
 * point is removed, nudging users onto the catalog-driven config screen.
 */
export const hideLegacyConfigsEnabled = () =>
  posthog.isFeatureEnabled("hideLegacyConfigs");

/**
 * The single rule for "should this config be offered to the user?". A config is
 * visible when the hide-legacy-configs flag is off OR the config was generated
 * by the catalog screen (carries the `generatedFromCatalog` marker). Legacy
 * file-uploaded configs — which have no marker — are hidden when the flag is on.
 *
 * Shared by the config pickers (via the `availableConfigs` singleton), the
 * quick-create auto-select, and the can-create-protocol gate so they can never
 * disagree about what's visible. Flag off → returns true for everything, i.e. a
 * pure no-op.
 */
export const isVisibleConfig = (config: {
  generatedFromCatalog?: boolean;
}): boolean => !hideLegacyConfigsEnabled() || config.generatedFromCatalog === true;
